<template>
  <div class="flex flex-col lg:flex-row p-6 m-auto">
    <!-- 📌 Sidebar Toggle Button for Mobile -->
    <!-- <button
      class="lg:hidden bg-blue-500 text-white px-4 py-2 rounded-md mb-4"
      @click="sidebarOpen = !sidebarOpen"
    >
      ☰ History
    </button> -->

    <!-- 📌 Overlay when Sidebar is Open -->
    <div
      v-if="sidebarOpen"
      class="sidebar-overlay"
      @click="sidebarOpen = false"
    ></div>

    <!-- 📌 Sidebar Drawer -->
    <!-- <div
      class="lg:w-1/4 w-full shadow-md lg:relative fixed inset-y-0 left-0 z-20 h-full overflow-y-auto bg-white transform transition-transform lg:translate-x-0"
      :class="{
        '-translate-x-full': !sidebarOpen,
        'translate-x-0': sidebarOpen,
      }"
    >
      <button
        class="lg:hidden absolute top-4 right-4 text-gray-600"
        @click="sidebarOpen = false"
      >
        ✖
      </button>
      <HistorySidebar
        :history="history"
        @select="handleHistorySelect"
        @updateUrl="websiteUrl = $event"
      />
    </div> -->

    <!-- 📌 Main Content -->
    <div class="lg:w-3/4 w-full flex-1 p-4 pt-0 container mx-auto">
      <SubNavigation />

      <!-- 📌 Improved Search Input -->
      <WebsiteInput
        class="input-box"
        placeholder="Enter website URL..."
        @loading="setLoading"
        @results="handleResults"
        @reset="resetScan"
        @error="handleScanError"
        v-model="websiteUrl"
        :hasScanned="hasScanned"
      />

      <div
        v-if="scanError && !loading"
        class="flex justify-between items-center text-red-600 bg-red-50 p-6 rounded shadow mt-6"
      >
        <div>
          <h3 class="text-lg font-semibold mb-2">
            Oops! Something went wrong.
          </h3>
          <p>{{ scanError }}</p>
        </div>
        <button
          @click="scanError = ''"
          class="text-red-500 hover:text-red-700 p-1"
          aria-label="Dismiss scan error"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <div
        v-if="auditError && !loading"
        class="flex justify-between items-center text-amber-600 bg-amber-50 p-4 rounded shadow mt-4"
      >
        <div class="flex items-center gap-2">
          <AlertTriangle class="w-5 h-5 text-amber-500 flex-shrink-0" />
          <span>{{ auditError }}</span>
        </div>
        <div class="flex items-center gap-2">
          <button
            @click="retryAudit"
            :disabled="auditRetrying || auditLoading"
            class="px-3 py-1 bg-amber-100 border border-amber-300 text-amber-700 rounded text-sm hover:bg-amber-200 disabled:opacity-50"
          >
            {{ auditRetrying ? "Retrying…" : "Retry" }}
          </button>
          <button
            @click="auditError = ''"
            class="text-amber-500 hover:text-amber-700 p-1"
            aria-label="Dismiss audit error"
          >
            <X class="w-5 h-5" />
          </button>
        </div>
      </div>

      <!-- 📌 Tabs Navigation -->
      <div>
        <div class="tabs flex justify-center space-x-4">
          <button
            v-for="tab in tabs"
            :key="tab"
            class="tab"
            :class="{ active: activeTab === tab }"
            @click="activeTab = tab"
          >
            {{ tab }}
          </button>
        </div>
      </div>

      <!-- 📌 Tab Content -->
      <div
        v-if="activeTab === 'Diagnostic Results'"
        class="flex justify-center"
      >
        <ValidationResults
          v-if="results"
          :results="results"
          :auditResults="auditResults"
          :auditLoading="auditLoading"
          :googlebotLogData="googlebotLogData"
          @page-selected="handlePageSelected"
          @upload-access-log="uploadAccessLog"
          @re-scan="handleReScan"
          @scan-selected-pages="handleScanSelectedPages"
          ref="validationResults"
        />
      </div>

      <div v-if="activeTab === 'Design Comparison'" class="flex justify-center">
        <DesignComparison :cachedData="cachedDesignComparison" />
      </div>

      <div
        v-if="activeTab === 'Index by Google'"
        class="flex justify-center w-full"
      >
        <IndexedByGoogle
          :initialLogData="googlebotLogData"
          :uploading-prop="accessLogUploading"
          @upload-access-log="uploadAccessLog"
          @switch-tab="activeTab = $event"
        />
      </div>

      <!-- Illustration -->
      <div
        v-if="!results && !loading && activeTab === 'Diagnostic Results'"
        class="mt-10"
      >
        <img
          src="@/assets/illustrations/diagnostic-illustration.png"
          alt="Website Diagnostic Illustration"
          class="w-2/3 md:w-1/2 m-auto"
        />
      </div>
      <transition name="slide-expand">
        <div
          v-if="loading"
          class="w-full bg-blue-50 p-8 flex flex-col justify-center items-center text-center shadow-lg transition-all duration-500"
        >
          <h3 class="text-lg font-semibold mb-4">Analyzing Website Data...</h3>
          <p class="text-gray-600">{{ loadingInsight }}</p>
          <div class="progress-bar mt-4">
            <div class="progress" :style="{ width: progress + '%' }"></div>
          </div>

          <div class="w-full mt-4">
            <table class="w-full">
              <thead class="bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th class="px-2 py-1">#</th>
                  <th class="px-2 py-1">URL</th>
                  <th class="px-2 py-1">Title</th>
                  <th class="px-2 py-1">Meta Desc</th>
                  <th class="px-2 py-1">HTTPS</th>
                  <!-- <th class="px-2 py-1">Status</th> -->
                </tr>
              </thead>
              <tbody>
                <tr v-for="n in 5" :key="n" class="border-b">
                  <td class="px-2 py-1 text-center flex justify-center">
                    <div
                      class="animate-pulse bg-gray-300 h-4 w-4 rounded"
                    ></div>
                  </td>
                  <td class="px-2 py-1">
                    <div
                      class="animate-pulse bg-gray-300 h-4 w-full rounded"
                    ></div>
                  </td>
                  <td class="px-2 py-1">
                    <div
                      class="animate-pulse bg-gray-300 h-4 w-full rounded"
                    ></div>
                  </td>
                  <td class="px-2 py-1">
                    <div
                      class="animate-pulse bg-gray-300 h-4 w-full rounded"
                    ></div>
                  </td>
                  <td class="px-2 py-1">
                    <div
                      class="animate-pulse bg-gray-300 h-4 w-8 rounded"
                    ></div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import SubNavigation from "../components/SubNavigation.vue";
import WebsiteInput from "../components/WebsiteInput.vue";
import ValidationResults from "../components/ValidationResults.vue";
import DesignComparison from "../components/DesignComparison.vue";
import IndexedByGoogle from "../components/IndexedByGoogle.vue";
import axios from "axios";
import baseUrl from "../config";
import { normalizeUrl } from "../utils/urlUtils";
import { X, AlertTriangle } from "lucide-vue-next";

export default {
  components: {
    SubNavigation,
    WebsiteInput,
    ValidationResults,
    DesignComparison,
    IndexedByGoogle,
    X,
    AlertTriangle,
  },
  data() {
    return {
      websiteUrl: "",
      loading: false,
      progress: 0,
      loadingInsight: "Initializing...",
      insights: [
        "Checking server response time...",
        "Verifying SSL security...",
        "Analyzing website load performance...",
        "Scanning SEO metrics...",
        "Optimizing user experience...",
      ],
      results: null,
      auditResults: null,
      googlebotLogData: null,
      tabs: ["Diagnostic Results", "Design Comparison", "Index by Google"],
      activeTab: "Diagnostic Results",
      history: {},
      sidebarOpen: false, // 📌 Controls Sidebar Visibility
      scanError: "",
      auditError: "",
      auditRetrying: false,
      hasScanned: false,
      auditLoading: false,
      accessLogUploading: false,
    };
  },
  mounted() {
    this.fetchHistory();
  },
  computed: {
    filteredIssues() {
      // ✅ Ensure `results` exists and `issues` is an array before filtering
      if (!this.results || !Array.isArray(this.results.issues)) {
        return []; // Return an empty array if undefined
      }
      return this.results.issues.filter((issue) => issue.type === "error");
    },
    cachedDesignComparison() {
      return this.results?.design_comparison || null;
    },
  },
  methods: {
    async fetchHistory() {
      const token = localStorage.getItem("token");

      if (!token) {
        this.errorMessage = "You are not authenticated. Please log in.";
        this.loading = false;
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/diagnostic-history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.history = response.data;
      } catch (error) {
        console.error(
          "Failed to fetch history:",
          error.response?.data || error
        );
      }
    },
    handleHistorySelect(selectedResult) {
      this.websiteUrl = selectedResult.url;
      this.results = selectedResult;
      this.auditResults = selectedResult.audit_results || null;
      this.sidebarOpen = false; // 📌 Close sidebar on mobile after selecting

      // If audit results are missing (common for history items), try to fetch them
      if (!this.auditResults && this.websiteUrl) {
        this.runResourceAudit(this.websiteUrl);
      }
    },
    async handleResults(newResults) {
      this.loading = false;
      if (this.loadingInterval) {
        clearInterval(this.loadingInterval);
        this.loadingInterval = null;
      }
      // ✅ Ensure newResults is always valid
      this.results = {
        ...newResults,
        issues: Array.isArray(newResults.issues) ? newResults.issues : [],
      };

      const homepage = this.results.pages?.[0];
      if (homepage?.crawled === false) {
        if (homepage.scan_status === "blocked" || homepage.is_blocked) {
          this.scanError = `This website blocked the scan request (${
            homepage.scan_message || "Access denied"
          }). If you control the website, allowlist the scanner server's IP address. If you do not control it, ask the owner to allow scanner access or provide an accessible audit endpoint.`;
        } else if (homepage.scan_status === "skipped") {
          this.scanError = `Homepage crawl was skipped: ${
            homepage.scan_message || "Redirected to external resource."
          }`;
        } else {
          this.scanError = `Unable to crawl homepage: ${
            homepage.scan_message || "An unexpected error occurred."
          }`;
        }
      } else {
        this.scanError = "";
      }

      this.hasScanned = true;
      this.fetchHistory();

      // Clear log data from previous site
      this.googlebotLogData = null;

      // Automatically run resource audit
      await this.runResourceAudit(this.websiteUrl);
    },
    async handlePageSelected(url) {
      // Clear current audit results to trigger loading state
      this.auditResults = null;
      this.googlebotLogData = null;
      await this.runResourceAudit(url);
    },
    async handleReScan() {
      // Force fresh scan of the current URL
      const url = this.results?.pages?.[0]?.url || this.websiteUrl;
      if (url) {
        this.auditResults = null;
        this.googlebotLogData = null;
        await this.runResourceAudit(url, true);
      }
    },
    async handleScanSelectedPages(urls) {
      if (!urls || urls.length === 0) return;

      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await axios.post(
          `${baseUrl}/scan-selected-pages`,
          {
            base_url: this.websiteUrl,
            urls: urls,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        // Merge crawled pages back into existing results
        const existingPages = this.results?.pages || [];
        const newPages = response.data.pages || [];

        // Build merged list: keep already-crawled, replace stubs with newly crawled
        // (or skipped) pages, keep remaining stubs.
        const mergedPages = existingPages.map((p) => {
          if (p.crawled !== false) return p; // keep already crawled
          const newPage = newPages.find((np) => np.url === p.url);
          return newPage || p; // replace stub with crawled/skipped data, or keep stub
        });

        this.results = {
          ...this.results,
          pages: mergedPages,
          issues: Array.isArray(response.data.issues)
            ? response.data.issues
            : [],
          is_full_scan: false,
        };

        // Notify the child component that scanning is done
        this.$refs.validationResults?.handleScanComplete();
      } catch (err) {
        console.error("Scan selected pages failed:", err);
        // Notify child of failure
        this.$refs.validationResults?.handleScanSingleError();
        this.auditLoading = false;
      }
    },
    async runResourceAudit(url, forceRefresh = false) {
      const token = localStorage.getItem("token");
      if (!token) return;

      const trimmedUrl = normalizeUrl(url || "");
      if (!trimmedUrl) {
        this.auditError = "No valid URL to audit.";
        return;
      }

      // Store the URL we're auditing for potential retry
      this._lastAuditUrl = trimmedUrl;
      this._lastAuditForceRefresh = forceRefresh;
      this.auditError = "";
      this.auditLoading = true;

      try {
        const payload = { url: trimmedUrl };
        if (forceRefresh) {
          payload.refresh = 1;
        }
        const response = await axios.post(`${baseUrl}/audit-project`, payload, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000, // 2 minutes — audit can be slow with Playwright
        });
        this.auditResults = response.data;
        this.auditError = "";
      } catch (err) {
        console.error("Resource audit failed:", err);
        if (err.response?.data?.code === "target_http_error") {
          // The main validation scan can succeed even when this secondary
          // resource audit is blocked by the target site's firewall.
          this.auditError = "";
        } else if (
          err.code === "ECONNABORTED" ||
          err.message?.includes("timeout")
        ) {
          this.auditError =
            "The audit timed out. The website may be too slow to respond. You can retry, or try a different page.";
        } else if (err.response?.data?.error) {
          this.auditError = err.response.data.error;
        } else if (
          err.response?.status === 502 ||
          err.response?.status === 500
        ) {
          this.auditError =
            "The audit server could not reach the target website. The site may be down, too slow, or blocking our requests.";
        } else {
          this.auditError =
            "Failed to complete the resource audit. Please try again.";
        }
      } finally {
        this.auditLoading = false;
      }
    },
    async retryAudit() {
      if (!this._lastAuditUrl || this.auditRetrying) return;
      this.auditRetrying = true;
      try {
        await this.runResourceAudit(
          this._lastAuditUrl,
          this._lastAuditForceRefresh
        );
      } finally {
        this.auditRetrying = false;
      }
    },
    async uploadAccessLog(file) {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Please log in to upload files.");
        return;
      }

      this.accessLogUploading = true;
      const formData = new FormData();
      formData.append("access_log", file);

      try {
        const response = await axios.post(
          `${baseUrl}/audit/upload-access-log`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const data = response.data;
        if (data.success && data.googlebot_data) {
          this.googlebotLogData = data.googlebot_data;

          // Merge googlebot_data into existing crawl_indexing results
          if (this.auditResults?.results?.crawl_indexing?.indexed_by_google) {
            this.auditResults.results.crawl_indexing.indexed_by_google.googlebot_data =
              data.googlebot_data;
            this.auditResults = { ...this.auditResults };
          }
        } else if (data.error) {
          alert(data.error);
        }
      } catch (err) {
        console.error("Access log upload failed:", err);
        const status = err.response?.status;
        const serverError =
          err.response?.data?.error ||
          err.response?.data?.message ||
          (status === 413
            ? "The file is too large for the web server (HTTP 413). Please increase Nginx client_max_body_size and PHP upload_max_filesize/post_max_size."
            : status === 404
            ? "Upload route not found on server (HTTP 404). Please clear the route cache (php artisan route:clear)."
            : status === 401
            ? "Session expired or unauthorized (HTTP 401). Please log in again."
            : status === 500
            ? "Server error (HTTP 500) while processing the log file. Check Laravel log files."
            : null);
        alert(
          serverError ||
            `Failed to upload access log (${err.message || "Unknown error"}).`
        );
      } finally {
        this.accessLogUploading = false;
      }
    },
    handleScanError(errorMessage) {
      this.loading = false;
      if (this.loadingInterval) {
        clearInterval(this.loadingInterval);
        this.loadingInterval = null;
      }
      this.results = null;
      this.auditResults = null;
      this.googlebotLogData = null;
      this.scanError = errorMessage;
      this.auditError = "";
    },
    setLoading() {
      this.loading = true;
      this.startLoadingAnimation();
    },
    startLoadingAnimation() {
      let index = 0;
      this.progress = 0;
      if (this.loadingInterval) {
        clearInterval(this.loadingInterval);
      }

      this.loadingInterval = setInterval(() => {
        if (this.progress >= 100) {
          clearInterval(this.loadingInterval);
          this.loadingInterval = null;
        } else {
          this.progress += 5;
          // Rotate through your insights array
          this.loadingInsight = this.insights[index % this.insights.length];
          index++;
        }
      }, 3000);
    },
    resetScan() {
      // Clear everything for a new scan
      this.hasScanned = false;
      this.results = null;
      this.auditResults = null;
      this.googlebotLogData = null;
      this.websiteUrl = "";
      this.scanError = "";
      this.auditError = "";
      this._lastAuditUrl = null;
      if (this.loadingInterval) {
        clearInterval(this.loadingInterval);
        this.loadingInterval = null;
      }
    },
  },
};
</script>

<style scoped>
/* 📌 Sidebar Overlay */
.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 15;
  transition: opacity 0.3s ease-in-out;
}

.progress-bar {
  width: 80%;
  height: 10px;
  background: #ddd;
  border-radius: 5px;
  overflow: hidden;
}
.progress {
  height: 100%;
  background: #3498db;
  transition: width 3s ease;
}

/* Optional for the slide-expand transition */
.slide-expand-enter-active,
.slide-expand-leave-active {
  transition: all 3s ease;
}
.slide-expand-enter-from,
.slide-expand-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* 📌 Sidebar Drawer Transition for Mobile */
@media (max-width: 1024px) {
  .translate-x-0 {
    transform: translateX(0);
  }
  .-translate-x-full {
    transform: translateX(-100%);
  }
}

/* 📌 Improved Search Input */
/* .input-box {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 14px;
  transition: all 0.3s ease;
} */
.input-box:focus {
  border-color: #3b82f6;
  box-shadow: 0px 0px 8px rgba(59, 130, 246, 0.3);
}

/* 📌 Tabs Styling */
.tab {
  padding: 10px 16px;
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
}
.tab:hover {
  background-color: #f9fafb;
}
.tab.active {
  font-weight: bold;
}

/* 📌 Fade-In Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 📌 Mobile Optimization */
@media (max-width: 640px) {
  .tab {
    padding: 12px 18px;
  }
  .input-box {
    padding: 12px;
  }
}
</style>
